/**
 * workflowEventGroups - Agrupamento dos eventos do catálogo por módulo
 *
 * O catálogo do banco (`workflow_event_types`) usa códigos no formato
 * `<modulo>.<evento>` (ex.: `proposal.accepted`). Aqui traduzimos o prefixo
 * para um nome de módulo em português, para o designer exibir os eventos
 * agrupados (CRM, Proposta, Contrato, Financeiro, Tarefa, Conversa...).
 *
 * @version 1.2.1
 * RELEASE: 2026-10-02 v1.2.1 - Rótulos de Veículos e Vistorias (entidade Veículo).
 * RELEASE: 2026-09-12 v1.1.0 - Rótulos dos módulos de tarefa (tasks, retention,
 *          legal) para os eventos das novas entidades Tarefa/Retenção/Jurídico.
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (gatilhos do workflow v2)
 */

import type { WorkflowCatalogItem } from '@/hooks/useWorkflowDesigner';

/** Nome amigável de cada prefixo de evento. */
const MODULOS: Record<string, string> = {
  deal: 'CRM — Negócios',
  proposal: 'Propostas',
  contract: 'Contratos',
  billing: 'Financeiro',
  task: 'Tarefas',
  conversation: 'Atendimento',
  vehicle: 'Veículos',
  inspection: 'Vistorias',
  approval: 'Aprovações',
  timer: 'Temporizadores',
  integration: 'Integrações',
  workflow: 'Workflow',
};

/** Retorna o nome do módulo de um código de evento. */
export function moduloDoEvento(code: string): string {
  const prefixo = String(code || '').split('.')[0];
  return MODULOS[prefixo] ?? 'Outros';
}

/** Agrupa o catálogo de eventos por módulo, preservando a ordem dos módulos conhecidos. */
export function agruparEventosPorModulo(
  catalogo: WorkflowCatalogItem[],
): Array<{ modulo: string; eventos: WorkflowCatalogItem[] }> {
  const mapa = new Map<string, WorkflowCatalogItem[]>();

  // v1.1.0: códigos de módulo gravados no banco que têm rótulo próprio
  // v1.2.0: 'tasks' é sinônimo de 'task' (o catálogo foi unificado em 'task',
  // mas o sinônimo continua mapeado para não duplicar o grupo "Tarefas")
  const ROTULO_MODULO: Record<string, string> = {
    task: 'Tarefas',
    tasks: 'Tarefas',
    retention: 'Retenção',
    legal: 'Jurídico',
    // v1.2.1: entidade Veículo
    vehicle: 'Veículos',
    checklist: 'Vistorias',
  };

  for (const evento of catalogo) {
    const bruto = evento.module?.trim() || '';
    const modulo = ROTULO_MODULO[bruto] ?? (bruto || moduloDoEvento(evento.code));
    const atual = mapa.get(modulo) ?? [];
    atual.push(evento);
    mapa.set(modulo, atual);
  }

  const ordem = Object.values(MODULOS);
  return Array.from(mapa.entries())
    .sort((a, b) => {
      const ia = ordem.indexOf(a[0]);
      const ib = ordem.indexOf(b[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    })
    .map(([modulo, eventos]) => ({ modulo, eventos }));
}
